export function useVideoState({file, isPlaybackErrored = ref(false)}) {
  const userContext = useUserContext({entity: file});

  const STATES = {
    loading: 'LOADING',
    playable: 'PLAYABLE',
    errored: {
      icon: 'i-ri-error-warning-fill',
      title: 'Playback Unavailable',
      desc: `We're unable to play this video because it's in a format or codec that isn't supported by our system or may have an issue. Don't worry, your original file is safe and sound! You can download it using the tools above. For more details, check out our <a href="https://support.forever.com/hc/en-us/articles/32911932939547-Why-won-t-my-uploaded-video-play" target="_blank">Help Desk article</a>.`
    },
    playbackErrored: {
      icon: 'i-ri-error-warning-fill',
      title: 'Playback Error',
      desc: 'Something went wrong and we could not play your video. Please try again later.'
    },
    needsTranscoding: 'needsTranscoding',
    processing: {
      spin: true,
      icon: COMMON_ICONS.loading,
      title: 'Video Processing',
      desc: `Hang tight! We're getting your video ready for its first playback. It'll just take a moment.`,
    },
    needsPlan: {
      showThumb: true,
      title: 'Relive This Memory',
      desc: 'Stream your videos instantly and unlock simple tools for editing, <br>preserving, and sharing the moments that matter most.',
      showDownload: true,
      action: () => {
        useCommonModals().openVideoUpsellModal({
          context: VIDEO_UPSELL_CONTEXTS.videoPlayback
        });
      },
      actionText: 'View Plans'
    },
    unavailable: {
      icon: 'i-ri-play-circle-line',
      showThumb: true,
      title: 'Playback Unavailable',
      desc: 'Playback is unavailable for this video.',
      showDownload: true
    }
  };

  const currentState = computed(() => {
    const currentFile = file?.value || file;

    if (!currentFile?.is) {
      return STATES.loading;
    }

    //if errored, same state for all users
    if (currentFile.is.errored) {
      return STATES.errored;
    }

    if (isPlaybackErrored.value) {
      return STATES.playbackErrored;
    }

    //processing, display for all users
    if (currentFile.is.processing) {
      return STATES.processing;
    }

    //if streamable play for all users
    if (currentFile.is.streamable) {
      return STATES.playable;
    }

    //if we do not have the file detail record
    if (!currentFile.is.detail) {
      return STATES.loading;
    }

    //transcode, display for all users
    if (currentFile.user?.abilities?.includes('transcode_video')) {
      return STATES.needsTranscoding;
    }

    //if owner, needs a plan
    if (userContext.isEntityOwner.value) {
      return STATES.needsPlan;
    }

    //shared, unavailable
    return STATES.unavailable;
  });

  const isPlayable = computed(() => currentState.value === STATES.playable);
  const showNonPlayableState = computed(() => typeof currentState.value === 'object');

  return {
    STATES,
    currentState,
    isPlayable,
    showNonPlayableState
  };
}
